import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2, TriangleAlert } from "lucide-react";
import { useTranslation, type Locale } from "@ncp/i18n";
import { Badge, Button, Field, Input, NativeSelect, Textarea, Tooltip, cn } from "@ncp/ui";
import { playbooksFor, type AutomationAction, type AutomationConnectorOperation, type AutomationEditorOptions, type AutomationRef, type AutomationScope } from "./api";

const MAX_ACTIONS = 10;
const PRIORITIES = ["Low", "Medium", "High", "Critical"] as const;
const SET_FIELDS = ["priority", "summary"] as const;

const refName = (r: AutomationRef, locale: Locale) => (locale === "ar" && r.nameAr ? r.nameAr : r.name);
/** Interpolation values that keep `{{placeholder}}` tokens literal in hints and examples. */
const LITERAL = Object.fromEntries(["caseNumber", "title", "date", "eventLabel", "previousQuarter", "previousMonth"].map((p) => [p, `{{${p}}}`]));

interface Props {
  actions: AutomationAction[];
  onChange: (next: AutomationAction[]) => void;
  scope: AutomationScope;
  workspaceKey?: string | null;
  options: AutomationEditorOptions | undefined;
  readOnly: boolean;
  locale: Locale;
}

/** The ordered list of what an automation does; each action shows the fields its type takes, with pickers for agents, playbooks and recipients. */
export function ActionsEditor({ actions, onChange, scope, workspaceKey, options, readOnly, locale }: Props) {
  const { t } = useTranslation();
  const catalog = options?.catalog.actions ?? [];
  const allowed = catalog.filter((a) => a.scopes.includes(scope));
  const set = (i: number, next: AutomationAction) => onChange(actions.map((a, j) => (j === i ? next : a)));
  const move = (i: number, by: number) => {
    const next = [...actions];
    const [item] = next.splice(i, 1);
    next.splice(i + by, 0, item!);
    onChange(next);
  };
  const add = () => onChange([...actions, { type: allowed[0]?.type ?? "notification.send" }]);

  return (
    <div className="space-y-3">
      {actions.length === 0 ? <p className="rounded-xl border border-dashed border-sand-300 p-4 text-center text-sm text-muted-foreground">{t("admin:automations.actions.empty")}</p> : null}
      {actions.map((action, i) => {
        const meta = catalog.find((a) => a.type === action.type);
        const notAllowed = !!meta && !meta.scopes.includes(scope);
        return (
          <div key={i} className={cn("rounded-xl border p-3", notAllowed || !meta ? "border-danger-200 bg-danger-50/30" : "border-sand-200 bg-white/70")}>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" size="sm" className="tabular">{i + 1}</Badge>
              <NativeSelect value={action.type} disabled={readOnly} className="h-9 w-64" aria-label={t("admin:automations.actions.type")}
                onChange={(e) => set(i, { type: e.target.value })}>
                {!meta ? <option value={action.type}>{action.type}</option> : null}
                {(meta && notAllowed ? [meta, ...allowed] : allowed).map((a) => <option key={a.type} value={a.type}>{locale === "ar" ? a.labelAr : a.label}</option>)}
              </NativeSelect>
              {meta ? <span className="min-w-0 flex-1 text-xs text-muted-foreground">{locale === "ar" ? meta.descriptionAr : meta.description}</span> : <span className="flex-1" />}
              {!readOnly ? (
                <div className="flex items-center gap-0.5">
                  <Tooltip content={t("admin:automations.actions.up")}><Button size="icon-sm" variant="ghost" disabled={i === 0} aria-label={t("admin:automations.actions.up")} onClick={() => move(i, -1)}><ArrowUp /></Button></Tooltip>
                  <Tooltip content={t("admin:automations.actions.down")}><Button size="icon-sm" variant="ghost" disabled={i === actions.length - 1} aria-label={t("admin:automations.actions.down")} onClick={() => move(i, 1)}><ArrowDown /></Button></Tooltip>
                  <Tooltip content={t("admin:common.remove")}><Button size="icon-sm" variant="ghost" className="text-danger-600" aria-label={t("admin:common.remove")} onClick={() => onChange(actions.filter((_, j) => j !== i))}><Trash2 /></Button></Tooltip>
                </div>
              ) : null}
            </div>
            {notAllowed ? <p className="mt-1.5 flex items-center gap-1 text-xs text-danger-700"><TriangleAlert className="size-3" /> {t("admin:automations.actions.notAllowed", { scope: t(`admin:automations.scope.${scope}`) })}</p> : null}
            {meta?.needsCase ? <p className="mt-1 text-[11px] text-muted-foreground">{t("admin:automations.actions.needsCase")}</p> : null}
            {meta && meta.fields.length > 0 ? (
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {meta.fields.map((f) => <ActionField key={f} field={f} action={action} scope={scope} workspaceKey={workspaceKey} options={options} readOnly={readOnly} locale={locale} onChange={(v) => set(i, withField(action, f, v))} onAction={(next) => set(i, next)} />)}
              </div>
            ) : null}
          </div>
        );
      })}
      {!readOnly ? (
        <Button variant="outline" size="sm" disabled={actions.length >= MAX_ACTIONS} onClick={add}><Plus /> {t("admin:automations.actions.add")}</Button>
      ) : null}
    </div>
  );
}

function withField(action: AutomationAction, field: string, value: unknown): AutomationAction {
  const next: AutomationAction = { ...action };
  if (value === undefined || value === null || value === "" || (typeof value === "object" && !Array.isArray(value) && Object.keys(value as object).length === 0)) delete next[field];
  else next[field] = value;
  return next;
}

const str = (v: unknown) => (typeof v === "string" ? v : v == null ? "" : String(v));
const without = (action: AutomationAction, ...keys: string[]): AutomationAction => {
  const next: AutomationAction = { ...action };
  for (const k of keys) delete next[k];
  return next;
};
const CONNECTOR_CALL = "connector.call";

function ActionField({ field, action, scope, workspaceKey, options, readOnly, locale, onChange, onAction }: {
  field: string; action: AutomationAction; scope: AutomationScope; workspaceKey?: string | null; options: AutomationEditorOptions | undefined; readOnly: boolean; locale: Locale;
  onChange: (value: unknown) => void; onAction: (next: AutomationAction) => void;
}) {
  const { t } = useTranslation();
  const connectorCall = action.type === CONNECTOR_CALL;
  const labelKey = connectorCall && field === "title" ? "workbookTitle" : connectorCall && field === "input" ? "connectorInput" : field;
  const label = t(`admin:automations.fields.${labelKey}`, { defaultValue: field });
  const hint = t(`admin:automations.fields.${labelKey}Hint`, { defaultValue: "", ...LITERAL }) || undefined;
  const value = action[field];
  const connector = connectorCall ? options?.connectors.find((c) => c.key === action.connector) : undefined;
  const operation = connector?.operations.find((o) => o.key === action.operation);
  const readsOnly = scope !== "Platform";
  switch (field) {
    case "connector": {
      const connectors = options?.connectors ?? [];
      return (
        <Field label={label} required hint={connectors.length === 0 ? t("admin:automations.fields.connectorEmpty") : undefined}>
          <NativeSelect value={str(value)} disabled={readOnly} onChange={(e) => onAction({ ...without(action, "operation", "input"), ...(e.target.value ? { connector: e.target.value } : {}) })}>
            <option value="">—</option>
            {connectors.map((c) => <option key={c.key} value={c.key}>{refName(c, locale)}</option>)}
            {value && !connectors.some((c) => c.key === value) ? <option value={str(value)}>{str(value)}</option> : null}
          </NativeSelect>
        </Field>
      );
    }
    case "operation": {
      const operations = connector?.operations ?? [];
      return (
        <Field label={label} required hint={readsOnly ? t("admin:automations.fields.readOnlyOnly", { scope: t(`admin:automations.scope.${scope}`) }) : operation?.description ?? undefined}>
          <NativeSelect value={str(value)} disabled={readOnly || !connector} onChange={(e) => onAction(e.target.value ? { ...without(action, "input", "operation"), operation: e.target.value } : without(action, "input", "operation"))}>
            <option value="">—</option>
            {operations.map((o) => (
              <option key={o.key} value={o.key} disabled={readsOnly && !o.readOnly}>
                {locale === "ar" && o.nameAr ? o.nameAr : o.name}{o.readOnly ? "" : ` · ${t("admin:automations.fields.writes")}`}
              </option>
            ))}
            {value && !operations.some((o) => o.key === value) ? <option value={str(value)}>{str(value)}</option> : null}
          </NativeSelect>
        </Field>
      );
    }
    case "saveTo":
      return (
        <Field label={label} hint={operation && !operation.returnsTable ? t("admin:automations.fields.saveToNoTable") : undefined}>
          <NativeSelect value={str(value)} disabled={readOnly} onChange={(e) => onChange(e.target.value)}>
            <option value="">{t("admin:automations.fields.saveToNone")}</option>
            <option value="matter">{t("admin:automations.fields.saveToMatter")}</option>
          </NativeSelect>
        </Field>
      );
    case "subscription": {
      const hooks = options?.webhooks ?? [];
      return (
        <Field label={label} required hint={hooks.length === 0 ? t("admin:automations.fields.subscriptionEmpty") : undefined}>
          <NativeSelect value={str(value)} disabled={readOnly} onChange={(e) => onChange(e.target.value)}>
            <option value="">—</option>
            {hooks.map((h) => <option key={h.key} value={h.key}>{h.name}{h.group ? ` · ${h.group}` : ""}</option>)}
            {value && !hooks.some((h) => h.key === value) ? <option value={str(value)}>{str(value)}</option> : null}
          </NativeSelect>
        </Field>
      );
    }
    case "to":
      return <Field label={label} hint={hint}><RecipientPicker value={str(value)} scope={scope} roles={options?.roles ?? []} members={action.type === "digest.send"} readOnly={readOnly} locale={locale} onChange={onChange} /></Field>;
    case "text":
    case "brief":
      return <Field label={label} hint={hint} className="md:col-span-2"><TemplateText value={str(value)} placeholders={options?.catalog.placeholders ?? []} readOnly={readOnly} onChange={onChange} /></Field>;
    case "agent":
      return (
        <Field label={label} required>
          <NativeSelect value={str(value)} disabled={readOnly} onChange={(e) => onChange(e.target.value)}>
            <option value="">—</option>
            {(options?.agents ?? []).map((a) => <option key={a.key} value={a.key}>{refName(a, locale)}{a.group === "Compiled" ? ` · ${t("admin:agents.kind.Compiled")}` : ""}</option>)}
            {value && !(options?.agents ?? []).some((a) => a.key === value) ? <option value={str(value)}>{str(value)}</option> : null}
          </NativeSelect>
        </Field>
      );
    case "playbook": {
      const playbooks = playbooksFor(options, scope, workspaceKey, true);
      return (
        <Field label={label} required>
          <NativeSelect value={str(value)} disabled={readOnly} onChange={(e) => onChange(e.target.value)}>
            <option value="">—</option>
            {playbooks.map((p) => <option key={p.key} value={p.key}>{refName(p, locale)}</option>)}
            {value && !playbooks.some((p) => p.key === value) ? <option value={str(value)}>{str(value)}</option> : null}
          </NativeSelect>
        </Field>
      );
    }
    case "recordType":
    case "template":
    case "service": {
      const refs = field === "recordType" ? options?.recordTypes ?? [] : field === "template" ? options?.letterTemplates ?? [] : playbooksFor(options, scope, workspaceKey, true);
      return (
        <Field label={label} required>
          <NativeSelect value={str(value)} disabled={readOnly} onChange={(e) => onChange(e.target.value)}>
            <option value="">—</option>
            {refs.map((r) => <option key={r.key} value={r.key}>{refName(r, locale)}</option>)}
            {value && !refs.some((r) => r.key === value) ? <option value={str(value)}>{str(value)}</option> : null}
          </NativeSelect>
        </Field>
      );
    }
    case "fields":
    case "values":
    case "intake":
    case "parameters":
      return <Field label={label} hint={hint} className="md:col-span-2"><KeyValueEditor value={(value as Record<string, unknown> | undefined) ?? {}} readOnly={readOnly} onChange={onChange} /></Field>;
    case "input":
      if (connectorCall)
        return <Field label={label} hint={hint} className="md:col-span-2"><ConnectorInputs operation={operation} value={value} readOnly={readOnly} locale={locale} onChange={onChange} /></Field>;
      return <Field label={label} hint={hint} className="md:col-span-2"><JsonText value={value} readOnly={readOnly} onChange={onChange} /></Field>;
    case "priority":
      return (
        <Field label={label}>
          <NativeSelect value={str(value) || "High"} disabled={readOnly} onChange={(e) => onChange(e.target.value)}>
            {PRIORITIES.map((p) => <option key={p} value={p}>{t(`admin:automations.priority.${p}`)}</option>)}
          </NativeSelect>
        </Field>
      );
    case "field":
      return (
        <Field label={label} required>
          <NativeSelect value={str(value)} disabled={readOnly} onChange={(e) => onChange(e.target.value)}>
            <option value="">—</option>
            {SET_FIELDS.map((f) => <option key={f} value={f}>{t(`admin:automations.setField.${f}`)}</option>)}
          </NativeSelect>
        </Field>
      );
    default:
      return <Field label={label} hint={hint}><Input value={str(value)} readOnly={readOnly} dir={field === "title" ? undefined : "ltr"} className={field === "title" ? undefined : "font-mono"} onChange={(e) => onChange(e.target.value)} /></Field>;
  }
}

/** Who receives a notification or an assignment: the owner, the assignee, a role, or a named person (`user:{id}`). */
function RecipientPicker({ value, scope, roles, members = false, readOnly, locale, onChange }: { value: string; scope: AutomationScope; roles: AutomationRef[]; members?: boolean; readOnly: boolean; locale: Locale; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const presets = ["", ...(scope === "Personal" ? ["owner"] : []), ...(members ? ["members"] : []), "assignee", ...roles.map((r) => `role:${r.key}`)];
  const custom = value !== "" && !presets.includes(value);
  const [editing, setEditing] = useState(custom);
  return (
    <div className="space-y-1.5">
      <NativeSelect value={editing || custom ? "__custom" : value} disabled={readOnly}
        onChange={(e) => { if (e.target.value === "__custom") { setEditing(true); onChange(value.startsWith("user:") ? value : "user:"); } else { setEditing(false); onChange(e.target.value); } }}>
        <option value="">{t("admin:automations.recipient.default")}</option>
        {scope === "Personal" ? <option value="owner">{t("admin:automations.recipient.owner")}</option> : null}
        {members ? <option value="members">{t("admin:automations.recipient.members")}</option> : null}
        <option value="assignee">{t("admin:automations.recipient.assignee")}</option>
        <optgroup label={t("admin:automations.recipient.roles")}>
          {roles.map((r) => <option key={r.key} value={`role:${r.key}`}>{refName(r, locale)}</option>)}
        </optgroup>
        <option value="__custom">{t("admin:automations.recipient.person")}</option>
      </NativeSelect>
      {editing || custom ? <Input value={value} dir="ltr" className="h-9 font-mono" placeholder="user:01a0…" readOnly={readOnly} onChange={(e) => onChange(e.target.value.trim())} /> : null}
    </div>
  );
}

/** Message text with the placeholders the engine fills in (`{{caseNumber}}`, `{{previousQuarter}}`…) one click away. */
function TemplateText({ value, placeholders, readOnly, onChange }: { value: string; placeholders: string[]; readOnly: boolean; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const insert = (name: string) => {
    const el = ref.current;
    const token = `{{${name}}}`;
    const at = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? at;
    onChange(value.slice(0, at) + token + value.slice(end));
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(at + token.length, at + token.length); });
  };
  return (
    <div className="space-y-1.5">
      <Textarea ref={ref} value={value} readOnly={readOnly} className="min-h-[64px]" placeholder={t("admin:automations.fields.textPlaceholder", LITERAL)} onChange={(e) => onChange(e.target.value)} />
      {!readOnly && placeholders.length ? (
        <div className="flex flex-wrap items-center gap-1">
          <span className="me-1 text-[11px] text-muted-foreground">{t("admin:automations.fields.placeholders")}</span>
          {placeholders.map((p) => <button key={p} type="button" onClick={() => insert(p)} className="rounded-md border border-sand-200 bg-sand-50 px-1.5 py-0.5 font-mono text-[11px] text-aeblack-800 hover:border-aegold-300 hover:bg-aegold-50" dir="ltr">{`{{${p}}}`}</button>)}
        </div>
      ) : null}
    </div>
  );
}

/** Playbook parameters as name/value rows; values may use placeholders (`period` = `{{previousQuarter}}`). */
type Pair = [string, string];
const pairsOf = (value: Record<string, unknown>): Pair[] => Object.entries(value).map(([k, v]) => [k, str(v)]);
const objectOf = (rows: Pair[]) => Object.fromEntries(rows.filter(([k]) => k.trim()).map(([k, v]) => [k.trim(), v]));

function KeyValueEditor({ value, readOnly, onChange }: { value: Record<string, unknown>; readOnly: boolean; onChange: (v: Record<string, string>) => void }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Pair[]>(() => pairsOf(value));
  const external = JSON.stringify(value);
  useEffect(() => {
    if (JSON.stringify(objectOf(rows)) !== external) setRows(pairsOf(JSON.parse(external) as Record<string, unknown>));
  }, [external]); // eslint-disable-line react-hooks/exhaustive-deps
  const update = (next: Pair[]) => { setRows(next); onChange(objectOf(next)); };
  return (
    <div className="space-y-1.5">
      {rows.map(([k, v], i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={k} dir="ltr" className="h-9 w-48 font-mono" placeholder={t("admin:automations.fields.paramName")} readOnly={readOnly}
            onChange={(e) => update(rows.map((r, j): Pair => (j === i ? [e.target.value, r[1]] : r)))} />
          <Input value={v} dir="ltr" className="h-9 flex-1 font-mono" placeholder="{{previousQuarter}}" readOnly={readOnly}
            onChange={(e) => update(rows.map((r, j): Pair => (j === i ? [r[0], e.target.value] : r)))} />
          {!readOnly ? <Button size="icon-sm" variant="ghost" className="text-danger-600" aria-label={t("admin:common.remove")} onClick={() => update(rows.filter((_, j) => j !== i))}><Trash2 /></Button> : null}
        </div>
      ))}
      {!readOnly ? <Button size="sm" variant="ghost" onClick={() => setRows([...rows, ["", ""]])}><Plus /> {t("admin:automations.fields.addParam")}</Button> : null}
    </div>
  );
}

/** One input per parameter of the chosen operation; an empty value falls back to the parameter's default when the automation runs. */
function ConnectorInputs({ operation, value, readOnly, locale, onChange }: {
  operation: AutomationConnectorOperation | undefined; value: unknown; readOnly: boolean; locale: Locale; onChange: (v: Record<string, string>) => void;
}) {
  const { t } = useTranslation();
  const values = (value && typeof value === "object" && !Array.isArray(value) ? value : {}) as Record<string, unknown>;
  if (!operation) return <p className="text-xs text-muted-foreground">{t("admin:automations.fields.pickConnectorFirst")}</p>;
  if (operation.parameters.length === 0) return <p className="text-xs text-muted-foreground">{t("admin:automations.fields.noParameters")}</p>;
  const update = (name: string, v: string) => {
    const next: Record<string, string> = Object.fromEntries(Object.entries(values).map(([k, x]) => [k, str(x)]));
    if (v.trim()) next[name] = v; else delete next[name];
    onChange(next);
  };
  return (
    <div className="grid gap-2 rounded-lg border border-sand-200 bg-sand-50/40 p-2.5 md:grid-cols-2">
      {operation.parameters.map((p) => {
        const name = locale === "ar" && p.labelAr ? p.labelAr : p.label ?? p.name;
        const hint = [p.description, p.default ? t("admin:automations.fields.defaultValue", { value: p.default, interpolation: { escapeValue: false } }) : null].filter(Boolean).join(" · ");
        return (
          <Field key={p.name} label={`${name} (${p.name})`} required={p.required && !p.default} hint={hint || undefined}>
            <Input value={str(values[p.name])} dir="ltr" className="h-9 font-mono" placeholder={p.default ?? p.example ?? ""} readOnly={readOnly} onChange={(e) => update(p.name, e.target.value)} />
          </Field>
        );
      })}
    </div>
  );
}

function JsonText({ value, readOnly, onChange }: { value: unknown; readOnly: boolean; onChange: (v: unknown) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => (value === undefined ? "" : JSON.stringify(value, null, 2)));
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="space-y-1">
      <Textarea value={text} readOnly={readOnly} dir="ltr" className="min-h-[72px] font-mono text-xs" placeholder='{ "caseNumber": "{{caseNumber}}" }'
        onChange={(e) => {
          setText(e.target.value);
          if (!e.target.value.trim()) { setError(null); onChange(undefined); return; }
          try { onChange(JSON.parse(e.target.value)); setError(null); } catch { setError(t("admin:automations.fields.invalidJson")); }
        }} />
      {error ? <p className="text-[11px] text-danger-700">{error}</p> : null}
    </div>
  );
}
